<template>
  <div id="selection-popup" class="smart-press-popup" :style="popupStyle" ref="popup">
    <DragHandle @start-drag="startDrag" class="tooltip tooltip-top" data-tip="Drag to move" />
    
    <div v-if="selectedText && !isGenerating && !showResult" class="smart-selecteted-text">
      <strong>Selected Text:</strong> 
      {{ displayText }}
    </div>
    
    <div v-if="selectedText && !isGenerating && !showResult" class="smart-refrence-selected">
      <input type="checkbox" v-model="referenceSelected" id="smart-press-refrence-checkbox"/>
 
      Reference Selected Text
    </div>
    
    <ContentGenerator 
      :selected-text="selectedText"
      :input-type="inputType"
      :reference-selected="referenceSelected"
      :prompt="prompt"
      :show-templates-panel="showTemplatesPanel"
      :show-history-panel="showHistoryPanel"
      :custom-fields="customFields"
      :variables-panel="$refs.variablesPanel"
      @toggle-generating="isGenerating = $event"
      @update:prompt="prompt = $event"
      @toggle-templates="toggleTemplates"
      @close-templates="showTemplatesPanel = false"
      @close-history="showHistoryPanel = false"
      @close-variables="showVariablesPanel = false"
      @toggle-history="toggleHistory"
      @save-prompt="savePromptToHistory"
      @save-content-history="saveContentToHistory"
      @toggle-history-type="updateHistoryType"
      @append-content="appendContent"
      @replace-content="replaceContent"
      ref="contentGenerator"
    />

    <a class="absolute h-[20px] w-[20px] bg-secondary rounded-lg flex items-center justify-center top-[-10px] right-[-10px] cursor-pointer !text-white" @click.prevent="$emit('close')">X</a>
    
    <TemplatesPanel 
      :show="showTemplatesPanel"
      :templates="templates"
      @close="toggleTemplates"
      @select-template="selectTemplate"
    />

    <VariablesPanel 
      :show="showVariablesPanel"
      :variables="variables"
      :prompt="prompt"
      ref="variablesPanel"
    />

    <HistoryPanel 
      :show="showHistoryPanel"
      :prompt-history="promptHistory"
      :content-history="contentHistory"
      :history-type="activeHistoryType"
      @close="toggleHistory"
      @select-history-item="selectHistoryItem"
      @toggle-history-type="updateHistoryType"
    />
  </div>
</template>

<script>
import axios from 'axios';
import DragHandle from './smart-press/DragHandle.vue';
import TemplatesPanel from './smart-press/TemplatesPanel.vue';
import VariablesPanel from './smart-press/VariablesPanel.vue';
import HistoryPanel from './smart-press/HistoryPanel.vue';
import ContentGenerator from './smart-press/ContentGenerator.vue';

export default {
  name: 'SmartPressPopup',
  components: {
    DragHandle,
    TemplatesPanel,
    VariablesPanel,
    HistoryPanel,
    ContentGenerator
  },
  props: {
    selectedText: String,
    position: Object,
    inputType: String,
    targetElement: [Object, HTMLElement]
  },
  data() {
    return {
      customFields: localStorage.getItem('smartpress_custom_fields') ? JSON.parse(localStorage.getItem('smartpress_custom_fields')) : {},
      referenceSelected: true,
      prompt: '',
      isGenerating: false,
      showResult: false,
      generatedContent: '',
      showTemplatesPanel: false,
      showVariablesPanel: false,
      showHistoryPanel: false,
      templates: [],
      isDragging: false,
      dragOffset: { x: 0, y: 0 },
      currentPosition: { top: 0, left: 0 },
      promptHistory: [],
      contentHistory: [],
      activeHistoryType: 'prompts',
      currentTypingPrompt: '',
      wpUser: window.smartpress_wpUserData ? window.smartpress_wpUserData : {}
    }
  },
  computed: {
    popupStyle() {
      return {
        position: 'absolute',
        top: `${this.currentPosition.top}px`,
        left: `${this.currentPosition.left}px`,
        zIndex: 10000
      }
    },
    displayText() {
      if (this.selectedText.length > 200) {
        return this.selectedText.substring(0, 50) + 
               ' <b>..................</b> ' + 
               this.selectedText.substring(this.selectedText.length - 50);
      }
      return this.selectedText;
    },
    variables() {
      // Extract all variables in the format {{ variableName }}
      const regex = /\{\{\s*([^}]+)\s*\}\}/g;
      const matches = [...this.prompt.matchAll(regex)];
      
      // Extract the variable names and remove whitespace
      const variables = matches.map(match => match[1].trim());
      
      // Remove duplicates by converting to Set and back to Array
      return [...new Set(variables)];
    }
  },
  mounted() {
    if(localStorage.getItem('smartPressPrompt')){
      this.prompt = localStorage.getItem('smartPressPrompt');
    }
    
    // Initialize currentPosition from props
    this.currentPosition = {
      top: this.position.top,
      left: this.position.left
    };
    
    // Add global event listeners for dragging
    document.addEventListener('mousemove', this.onDrag);
    document.addEventListener('mouseup', this.stopDrag);

    // Load history from localStorage - updated method
    this.loadHistories();
    
    // Start tracking current prompt for history
    this.currentTypingPrompt = this.prompt;
    
    // Check for saved history type preference
    const savedHistoryType = localStorage.getItem('smartPressHistoryType');
    if (savedHistoryType) {
      this.activeHistoryType = savedHistoryType;
    }
  },
  beforeUnmount() {
    // Clean up event listeners when component is destroyed
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.stopDrag);
  },
  created() {
    if(!this.customFields.api_key || this.customFields.api_key.length < 1){
    this.fetchCustomFields().then(async () => {
      this.updating = "";
      if(this.customFields.api_key && this.customFields.api_key.length > 1){
        this.updating = "Fetching Account Templates";
        this.fetchTemplates();
      }
    });
  }else{
    this.fetchTemplates();
  }

  },
  methods: {
    async fetchCustomFields() {
      this.loading = true;
      this.error = null;
      
      let baseUrl = this.wpUser.site_url;
      try {
        const response = await fetch(`${baseUrl}/wp-json/smartpress/v1/custom-fields`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': window.smartpress_wpApiSettings?.nonce || ''
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch custom fields');
        }
        
        const data = await response.json();
        this.customFields = data;
        console.log(data);
      } catch (error) {
        console.error('Error fetching custom fields:', error);
        this.error = 'Failed to load Account Info, Please contact support.';
      } finally {
        this.loading = false;
      }
    },
    
    appendContent(content) {
      this.$emit('append-content', content);
      this.$emit('close');
    },
    
    replaceContent(content) {
      this.$emit('replace-content', content);
      this.$emit('close');
    },
    
    toggleTemplates() {
      this.showTemplatesPanel = !this.showTemplatesPanel;
      if(this.showTemplatesPanel){
        this.showHistoryPanel = false;
      }else{
        this.fetchTemplates();
      }
    },

    toggleHistory() {
      this.showHistoryPanel = !this.showHistoryPanel;
      if (this.showHistoryPanel) {
        this.showTemplatesPanel = false;
      }
    },
    
    selectTemplate(template) {
      this.prompt = template.prompt;
      this.showTemplatesPanel = false;
    },
    
    // Drag methods
    startDrag(event) {
      // Only start drag on left mouse button
      if (event.button !== 0) return;
      
      this.isDragging = true;
      
      // Get the position of the click relative to the popup
      this.dragOffset = {
        x: event.clientX - this.currentPosition.left,
        y: event.clientY - this.currentPosition.top
      };
      
      // Prevent text selection during drag
      event.preventDefault();
    },
    
    onDrag(event) {
      if (!this.isDragging) return;
      
      // Calculate new position
      this.currentPosition = {
        top: event.clientY - this.dragOffset.y,
        left: event.clientX - this.dragOffset.x
      };
    },
    
    stopDrag(event) {
      if (this.isDragging) {
        if (event) {
          event.preventDefault();
        }
        
        // Store current scroll position
        const scrollPosition = {
          x: window.scrollX || window.pageXOffset,
          y: window.scrollY || window.pageYOffset
        };
        
        // Use setTimeout to ensure this happens after any browser default behaviors
        setTimeout(() => {
          // Restore scroll position
          window.scrollTo(scrollPosition.x, scrollPosition.y);
        }, 0);
      }
      
      this.isDragging = false;
    },

    // Templates
    async fetchTemplates(){
      try{
        await axios.get("https://67d16eab6a70f6dfc533.10g8.cloud/templates", {
          params:{
            session: this.customFields.api_key,
          }
        }).then(response => {
          if(response.data.success){
            this.templates = response.data.templates.documents;
            this.updating = "";
          }
        });
      } catch(error) {
        this.updating = "Retrying to fetch templates";
        // setTimeout(() => {
        //   this.fetchTemplates();
        // }, 1000);
      }
    },
    
    // Updated method to load both histories
    loadHistories() {
      try {
        // Load prompt history
        const savedPromptHistory = localStorage.getItem('smartPressPromptHistory');
        if (savedPromptHistory) {
          this.promptHistory = JSON.parse(savedPromptHistory);
        } else {
          // Try to migrate old history format if it exists
          const oldHistory = localStorage.getItem('smartPressHistory');
          if (oldHistory) {
            const oldHistoryArray = JSON.parse(oldHistory);
            // Convert old format to new format
            this.promptHistory = oldHistoryArray.map(text => ({
              text,
              timestamp: new Date().toISOString()
            }));
            // Save in new format
            localStorage.setItem('smartPressPromptHistory', JSON.stringify(this.promptHistory));
            // Remove old format after migration
            localStorage.removeItem('smartPressHistory');
          } else {
            this.promptHistory = [];
          }
        }
        
        // Load content history
        const savedContentHistory = localStorage.getItem('smartPressContentHistory');
        if (savedContentHistory) {
          this.contentHistory = JSON.parse(savedContentHistory);
        } else {
          this.contentHistory = [];
        }
      } catch (e) {
        console.error('Error loading history from localStorage', e);
        this.promptHistory = [];
        this.contentHistory = [];
      }
    },
    
    // Updated method to save prompt to history
    savePromptToHistory(prompt) {
      if (!prompt || prompt.trim() === '') return;
      
      // Create history item
      const historyItem = {
        text: prompt,
        timestamp: new Date().toISOString()
      };
      
      // Check if this prompt is different from the last one in history
      const lastPrompt = this.promptHistory[0]?.text;
      if (lastPrompt !== prompt) {
        // Add to the beginning of the array
        this.promptHistory.unshift(historyItem);
        
        // Keep only the last 50 items (or user preference)
        const maxLength = parseInt(localStorage.getItem('smartPressHistoryMaxLength') || '50', 10);
        if (this.promptHistory.length > maxLength) {
          this.promptHistory = this.promptHistory.slice(0, maxLength);
        }
        
        // Save to localStorage
        try {
          localStorage.setItem('smartPressPromptHistory', JSON.stringify(this.promptHistory));
        } catch (e) {
          console.error('Error saving prompt history to localStorage', e);
        }
      }
    },
    
    // New method to save content to history
    saveContentToHistory(content) {
      if (!content || content.trim() === '') return;
      
      // Create history item
      const historyItem = {
        text: content,
        timestamp: new Date().toISOString()
      };
      
      // Check if this content is different from the last one in history
      const lastContent = this.contentHistory[0]?.text;
      if (lastContent !== content) {
        // Add to the beginning of the array
        this.contentHistory.unshift(historyItem);
        
        // Keep only the max length items (or user preference)
        const maxLength = parseInt(localStorage.getItem('smartPressHistoryMaxLength') || '50', 10);
        if (this.contentHistory.length > maxLength) {
          this.contentHistory = this.contentHistory.slice(0, maxLength);
        }
        
        // Save to localStorage
        try {
          localStorage.setItem('smartPressContentHistory', JSON.stringify(this.contentHistory));
        } catch (e) {
          console.error('Error saving content history to localStorage', e);
        }
      }
    },
    
    // Update history type
    updateHistoryType(type) {
      this.activeHistoryType = type;
      localStorage.setItem('smartPressHistoryType', type);
    },
    
    // Updated method to select a history item
    selectHistoryItem(text, type) {
      if (type === 'prompts') {
        this.prompt = text;
      } else {
        // If it's content, we need to handle differently - perhaps add to editor
        if (this.$refs.contentGenerator) {
          this.$refs.contentGenerator.generatedContent = text;
          this.$refs.contentGenerator.showResult = true;
        }
      }
      this.showHistoryPanel = false;
    },
  },
  watch: {
    // Watch prompt changes
    prompt: {
      handler(newVal) {
        // Update variables panel visibility
        if(newVal.includes("{{")){
          this.showVariablesPanel = true;
        } else {
          this.showVariablesPanel = false;
        }
      }
    },
  }
}
</script>

<style scoped>
.smart-press-popup {
  position: relative;
  overflow: visible;
  user-select: none; /* Prevent text selection during drag */
}

.pointer-events-none {
  pointer-events: none;
}
</style> 